import { CommonModule, DatePipe } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { nlLocale } from 'ngx-bootstrap/locale';
import { TablesComponent } from '../../components/tables/tables.component';
import { UserSessionModel } from '../../../core/models/user/current-user.model';
import { StatusUtils } from '../../../utils/status-utils';
import { FilterModel } from '../../../core/models/filters/filters.model';
import { CurrentUserService } from '../../../core/services/user/current-user.service';
import { PatientsService } from '../../../core/services/patient/patients.service';
import { LanguageService } from '../../../core/services/language.service';
import { ColumnModel } from '../../../core/models/table/column.model';
import { SortModel } from '../../../core/models/filters/sort.model';
import { DateFormatterUtil } from '../../../utils/date-format-utils';
import { ConsultationsService } from '../../../core/services/consultation/consultations.service';
import { ToastService } from '../../../core/services/toast.service';
import { UserRole } from '../../../../enums/roles.enum';
import { environment } from '../../../../environments/environment';
defineLocale('nl', nlLocale);

@Component({
  selector: 'app-work-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    BsDatepickerModule,
    TablesComponent,
  ],
  templateUrl: './referral.component.html',
  styleUrl: './referral.component.scss',
  providers: [DatePipe, provideAnimations()],
})
export class ReferralComponent {
  currentUser: UserSessionModel | null = null;
  referralList: any = [];
  referralListLoading: boolean = true;
  totalPage: number = 0;
  totalReferralListCount: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  pageHeight: number;
  currentPage: number = 1;
  organizationList: any = [];
  caseStatuses = StatusUtils?.getFilterCaseStatuses();
  maxDate = new Date();
  url: string = `${environment.apiLocations.patient}/PatientRecord/GetReferralRequiredPatients`;
  filters: FilterModel[] = [
    //AGB Code
    {
      type: 'input',
      label: 'dermNo',
      placeholder: 'filterByDermNo',
      filterId: 'PIDNo',
      value: null,
    },
    //StartDate
    {
      type: 'date',
      label: 'date',
      placeholder: 'dd-mm-yyyy',
      filterId: 'SelectedDateTime',
      value: null,
    },

    //PatientName
    {
      type: 'input',
      label: 'patientName',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //CustomerCode
    {
      type: 'select',
      label: 'customerCode',
      placeholder: 'selectCustomerCode',
      filterId: 'CustomerCode',
      value: null,
    },
    //Status
    {
      type: 'select',
      label: 'status',
      placeholder: 'selectStatus',
      filterId: 'Status',
      value: null,
    },
  ];
  sort: SortModel | null = null;
  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  columnWidths: string[] = [
    'w-80',
    'w-125',
    'w-200',
    'w-150',
    'w-150',
    'w-125',
  ];

  constructor(
    private currentUserService: CurrentUserService,
    private patientsService: PatientsService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private bsLocaleService: BsLocaleService,
    private consultationsService: ConsultationsService,
    private router: Router,
    private toastService: ToastService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    this.bsLocaleService.use(userLang);

    this.columns = [
      { key: 'pidNo', translateKey: 'dermNo' },
      { key: 'createdDate', translateKey: 'createdDate' },
      { key: 'patientName', translateKey: 'patientName' },
      { key: 'customerCode', translateKey: 'customerCode' },
      { key: 'createdBy', translateKey: 'createdBy' },
      { key: 'patientStatus', translateKey: 'status' },
    ];
    setTimeout(() => {
      this.tableHeader = this.translate.instant('referralList');
      this.footerText = this.translate.instant('footerText.referrals');
    }, 100);
  }

  async ngOnInit() {
    await StatusUtils?.initializeTranslateService(this.translate);
    this.caseStatuses = StatusUtils?.getFilterWorkStatuses();
    this.currentUser = this.currentUserService.GetSession();
    if (
      this.currentUser?.role === UserRole.Admin ||
      this.currentUser?.role === UserRole.OrganizationAdmin ||
      this.currentUser?.role === UserRole.GeneralPractitioner
    ) {
      this.columns.push({
        key: 'actions',
        translateKey: 'actions',
        actionTypes: ['delete'],
      });
    }
    this.calculatePageSize();
    // this.getReferralList(this.currentPage, this.pageSize);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const newHeight = window.innerHeight;

    if (newHeight !== this.pageHeight) {
      this.calculatePageSize();
    }
  }

  private calculatePageSize(): void {
    const pageHeight = window.innerHeight;
    this.pageHeight = pageHeight;

    if (pageHeight < 600) {
      this.pageSize = 5;
    } else if (pageHeight < 1000) {
      this.pageSize = 10;
    } else if (pageHeight < 1400) {
      this.pageSize = 25;
    } else {
      this.pageSize = 50;
    }
    if (this.filters.some((filter) => !!filter.value)) {
      this.getReferralList(1, this.pageSize, this.filters);
    } else {
      this.getReferralList(this.currentPage, this.pageSize);
    }
  }

  filterReferralList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) => filter.filterId === 'SelectedDateTime' && filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getReferralList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getReferralList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getReferralList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getReferralList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): void {
    this.referralListLoading = true;
    this.consultationsService
      .referralList(page, size, filters, sort)
      .subscribe({
        next: (res) => {
          this.referralList = res.items;
          this.currentPage = page;
          this.totalReferralListCount = res.count;
          this.totalPage = res.pages;
          this.pageSize = res.size;
          this.referralListLoading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getReferralList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getReferralList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  checkPatientRecordInfo(referralItem): void {
    this.patientsService
      .patientInformationByEmail(referralItem.patientRecordId)
      .subscribe({
        next: (res) => {
          if (res?.isExist) {
            this.router.navigate([
              '/patient/appointment-detail',
              referralItem.patientId,
              referralItem.id,
            ]);
          } else {
            this.router.navigate([
              '/patient/appointment-detail',
              referralItem.id,
            ]);
          }
        },
      });
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'Patient.PidNo';
      case 'patientName':
        return 'Patient.Name';
      case 'customerCode':
        return 'CustomerCode';
      case 'createdBy':
        return 'CreatedUser';
      case 'patientStatus':
        return 'PatientRecordCases.PatientStatus';
      default:
        return column;
    }
  }

  actionButtonClick(actionType: string, row: any): void {
    switch (actionType) {
      case 'delete':
        this.deleteReferral(row.patientRecordId);
        break;
    }
  }

  deleteReferral(id: string): void {
    this.patientsService.deleteReferralPatient(id).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successDeleteReferralDesc'),
          'success'
        );
        this.getReferralList(1, this.pageSize, this.filters);
      },
    });
  }
}
